
import { Link } from "react-router-dom";
import { ArrowRight, Star, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRevealAnimation } from "@/hooks/use-reveal-animation";

interface Product {
  id: number;
  name: string;
  price: number;
  rating: number;
  image: string;
  slug: string;
}

interface FeaturedProductsProps {
  products: Product[];
}

const FeaturedProducts = ({ products }: FeaturedProductsProps) => {
  const isMobile = useIsMobile();
  
  return (
    <section className="py-10 md:py-16 bg-background">
      <div className="container-custom">
        <div className="flex justify-between items-center mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-bold">Featured Products</h2>
          <Link
            to="/shop"
            className="flex items-center text-primary hover:text-primary/80 transition-colors button-hover"
          >
            <span className="text-sm md:text-base">View all</span>
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
        
        {/* Enhanced product section heading */}
        <div className="text-center mb-6 md:mb-10 max-w-2xl mx-auto">
          <p className="text-sm md:text-base text-muted-foreground">
            We've tested hundreds of products so you don't have to. These are our top recommendations for healthier armpits.
          </p>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {products.map((product, index) => {
            const { ref, isVisible } = useRevealAnimation<HTMLAnchorElement>();
            return (
              <Link 
                key={product.id} 
                to={product.slug} 
                className="group hover-card"
                ref={ref}
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.5s ease-out ${index * 0.1}s`,
                }}
              >
                <div className="bg-white rounded-lg overflow-hidden shadow-md transition-all hover:shadow-lg">
                  <div className="aspect-square overflow-hidden image-hover relative">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Added badges to highlight products */}
                    {product.id === 3 && (
                      <div className="absolute top-2 right-2 md:top-3 md:right-3 bg-primary text-white text-xs font-bold px-2 py-1 md:px-3 md:py-1 rounded-full">
                        BEST SELLER
                      </div>
                    )}
                    {product.id === 4 && (
                      <div className="absolute top-2 right-2 md:top-3 md:right-3 bg-yellow-500 text-white text-xs font-bold px-2 py-1 md:px-3 md:py-1 rounded-full">
                        TOP RATED
                      </div>
                    )}
                  </div>
                  <div className="p-3 md:p-6">
                    <h3 className="text-xs md:text-lg font-semibold mb-1 md:mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                    <div className="flex items-center mb-2 md:mb-3">
                      <div className="flex text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            fill={i < Math.floor(product.rating) ? "currentColor" : "none"}
                            className="h-2 w-2 md:h-4 md:w-4"
                          />
                        ))}
                      </div>
                      <span className="text-xs md:text-sm text-muted-foreground ml-1 md:ml-2">
                        {product.rating}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-base font-medium">${product.price.toFixed(2)}</span>
                      <Button size={isMobile ? "sm" : "default"} className="button-hover pulse-on-hover h-7 md:h-auto text-xs md:text-sm">
                        <ShoppingBag className="h-2 w-2 md:h-4 md:w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        
        {/* Added clear CTA button */}
        <div className="text-center mt-6 md:mt-10">
          <Button asChild size={isMobile ? "default" : "lg"} className="pulse-on-hover button-hover">
            <Link to="/shop">
              <ShoppingBag className="mr-2 h-4 w-4 md:h-5 md:w-5" />
              Shop All Products
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
